import { Asset, Program } from 'shared/api/retail';
import { AMPLIFY_DESMOS_MATH_ASSET } from 'shared/config/entitelment-constants';

export const PROGRAMS_ASSETS = {
  BoostReadingAqua: '47aecbc8-f648-4390-af71-bd39951fbdaa',
  BoostReading: '210915e8-955b-4624-bbe0-a05b7e710dca',
  BoostReadingGrade3: 'a507ecf4-d4c9-4a7e-8ade-1147c959adfc',
  BoostReadingGrade4: '0606029c-78bd-48c3-8372-ce2d51051919',
  BoostReadingGrade5: '285487bf-6d73-45f6-a05a-36dbef75ef9b',
  BoostTexasElementary: 'd790df98-adc0-4287-9d18-05b42758ee66',
  BoostLectura: '5a9d3e63-bc24-4cb0-9cbf-442f9d0b3368',
  BoostCloseReading: 'ad85a042-8d3a-4023-a751-96ffbb7961bc',
  BoostReadingTexasMiddleSchool: '46b89259-792b-4fbd-9529-2d3144149b0d',
  BoostReadingMiddleSchool: '02d32c5a-17d0-4d79-ae1e-9b24d90fe2a3',
  CKLAClassic: '7705006f-1c65-43fa-8cac-390077c922d7',
  CKLAHubGradeK: '546aa88f-edc3-48f1-a72e-e8b316001bab',
  CKLAHubGrade1: '1e644264-8c97-4b79-89ad-5bfb6634d048',
  CKLAHubGrade2: 'e155def6-9333-426f-b6c0-b66b52cf5914',
  CKLAHubGrade3: '5dc255d0-3f50-4c55-a3b2-cb7b6d4d08ae',
  CKLAHubGrade4: '2b79261f-87a5-4cb3-bb69-f433b6c585a7',
  CKLAHubGrade5: 'e01c88ac-eaf4-416f-b260-8f2c4b653075',
  ElementaryStudentApps: '6e74e98d-3e49-4612-8211-18779af0769a',
  mClassUnifiedAdminReporting: 'a9a18f50-09ba-45b2-bbb8-19e84959aa70',
  TexasELP: '20b2d235-7005-4991-bfec-a289b6439aa9',
  TexasELPSGrade1: '8af6b75c-8c04-4486-8338-95b073fcda29',
  TexasELPSGrade2: '9ed2d9a8-8252-46f1-86b9-80e0013a6660',
  TexasELPSGrade3: 'f13433e8-4bb5-4476-a349-397b6d1a6fa4',
  TexasELPSGrade4: '96dd4291-0b2f-43e0-b8f5-3540f0906883',
  TexasELPSGrade5: '34b15005-f87b-441f-8327-de5f5b507aee',
  Desmos: AMPLIFY_DESMOS_MATH_ASSET,
  DesmosNYC: 'b2097949-8e3f-4a92-9eca-6e975a11f815',
};

export const hasAsset = (assetBk: string, allAssets: Asset[]) =>
  allAssets.some(({ businessKey }) => businessKey === assetBk);

/**
 * Returns an array of assets from programs
 * considered Digital Experience or Interactive Classroom programs based on their metadata
 * */
export const collectDigitalExperienceAssets = (
  programs: Program[],
): string[] => {
  return programs.reduce((collectedAssets: string[], program) => {
    const { metadata } = program;

    const isDigitalExperienceProgram =
      !metadata.deTeacherClassicStudent && !!metadata['digital-experience'];
    const isInteractiveClassroomProgram =
      !metadata.icTeacherClassicStudent && !!metadata['interactive-classroom'];
    const deTeacherClassicStudent = !!metadata.deTeacherClassicStudent;
    const icTeacherClassicStudent = !!metadata.icTeacherClassicStudent;

    /*
     * If digital-experience/interactive-classroom and deTeacherClassicStudent/icTeacherClassicStudent
     * are both false then we still need to show assignment during walkthrough for such program.
     * It's because educators are able to assign lesson for programs with such flags combination.
     * */
    if (
      !isDigitalExperienceProgram &&
      !isInteractiveClassroomProgram &&
      (deTeacherClassicStudent || icTeacherClassicStudent)
    )
      return collectedAssets;

    const contentsAssets = program.contents
      .map((content) => content.assets)
      .flat()
      .filter<string>((asset): asset is string => asset !== null);

    return collectedAssets.concat(contentsAssets);
  }, []);
};

export const getAssetsSubset = (assets: Asset[], assetsIds: string[]) => {
  return assets.filter((asset) => assetsIds.includes(asset.businessKey));
};

export const findSubjectAsset = (assets: Asset[]) => {
  return assets.find((asset) => {
    const isDisplayed = asset.metadata.curriculum_app_display?.display;
    const hasSubject = asset.metadata.curriculum_app_display?.subject;
    const isEdition =
      asset.metadata.curriculum_app_display?.navCategory === 'editions';

    return isDisplayed && hasSubject && isEdition;
  });
};
